import "./EngineSchematic.css";
import { useEffect, useRef, useState } from "react";
import { ASSETS } from "../lib/constants";

const CAPTIONS = {
    TT:    { name: "T-AXIS",                    flow: "TT → E2 → IGF1 + DHT + Hct" },
    E2:    { name: "ESTROGEN MODULATION",       flow: "E2 ↔ TT + IGF1 + BP + BF%" },
    IGF1:  { name: "GROWTH AXIS",               flow: "E2 → GH → IGF-1 + HbA1c" },
    DHT:   { name: "PROSTATE AXIS",             flow: "DHT → PSA" },
    PSA:   { name: "PROSTATE AXIS",             flow: "DHT → PSA" },
    SHBG:  { name: "FREE-T DYNAMICS",           flow: "TT × SHBG → DHT + E2" },
    FT:    { name: "FREE-T DYNAMICS",           flow: "TT × SHBG → DHT + E2" },
    Hct:   { name: "CARDIO-METABOLIC CASCADE",  flow: "Hct + BF% + ApoB + CRP + HbA1c + BP" },
    BP:    { name: "CARDIO-METABOLIC CASCADE",  flow: "Hct + BF% + ApoB + CRP + HbA1c + BP" },
    CRP:   { name: "CARDIO-METABOLIC CASCADE",  flow: "Hct + BF% + ApoB + CRP + HbA1c + BP" },
    HbA1c: { name: "CARDIO-METABOLIC CASCADE",  flow: "Hct + BF% + ApoB + CRP + HbA1c + BP" },
    ApoB:  { name: "CARDIO-METABOLIC CASCADE",  flow: "Hct + BF% + ApoB + CRP + HbA1c + BP" },
    "BF%": { name: "CARDIO-METABOLIC CASCADE",  flow: "Hct + BF% + ApoB + CRP + HbA1c + BP" },
    GGT:   { name: "LIVER FUNCTION",            flow: "GGT · individual marker" },
    CysC:  { name: "KIDNEY FUNCTION",           flow: "Cystatin C · individual marker" },
    TSH:   { name: "THYROID AXIS",              flow: "TSH · individual marker" },
};
const DEFAULT_CAPTION = {
    name: "READY",
    flow: "hover any marker to reveal its clinical relationships",
};
const HUB_CAPTION = {
    name: "ENGINE",
    flow: "16 markers · 8 relationships · 2 ratios · live",
};

/* Snappy character-by-character reveal driven by requestAnimationFrame.
   When `active` flips to true the target string types in over `totalMs`;
   when it flips to false the visible text resets to empty (cursor returns
   to row 1). Uses RAF for smooth perception even at fast totals. */
function useTypewriter(target, active, totalMs = 150) {
    const [text, setText] = useState("");
    useEffect(() => {
        if (!active || !target) {
            setText("");
            return;
        }
        const start = performance.now();
        const len = target.length;
        let raf;
        const tick = () => {
            const elapsed = performance.now() - start;
            const ratio = Math.min(1, elapsed / totalMs);
            const count = Math.max(1, Math.floor(ratio * len));
            setText(target.slice(0, count));
            if (ratio < 1) raf = requestAnimationFrame(tick);
            else setText(target);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, active, totalMs]);
    return text;
}

/**
 * EngineSchematic — central diagram showing how TRT+ models work.
 * All styles scoped under `.cdo-schematic` so nothing leaks site-wide.
 *
 * Replaces an earlier static SVG diagram that lived at the bottom of TheBuild section.
 */
export default function EngineSchematic({ showHeader = true, centerOnly = false }) {
    const rootRef = useRef(null);
    const [caption, setCaption] = useState(DEFAULT_CAPTION);
    const [isActive, setIsActive] = useState(false);
    useEffect(() => {
        const el = rootRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        el.classList.add("cdo-anim-ready");
                        obs.disconnect();
                    }
                });
            },
            { threshold: 0.18 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    /* Tier 3 — wire up hover-highlight on the central network nodes.
       Each node defines its own "buddy list" (HOVER_GROUPS): hovering
       a node activates all of its buddies plus their radial arms when
       applicable. Most nodes are solo; some belong to clinical clusters
       (cardiometabolic, T-axis, IGF1↔E2, etc.). */
    useEffect(() => {
        const HOVER_GROUPS = {
            // ── Outer-ring markers (have radial arm to hub) ──
            TT:    ["TT", "E2", "IGF1", "DHT", "Hct"],
            DHT:   ["DHT", "PSA"],
            SHBG:  ["SHBG", "FT", "TT", "DHT", "E2"],
            Hct:   ["Hct", "BF%", "ApoB", "CRP", "HbA1c", "BP"],
            BP:    ["BP", "Hct", "BF%", "ApoB", "CRP", "HbA1c"],
            CRP:   ["CRP", "Hct", "BF%", "ApoB", "HbA1c", "BP"],
            HbA1c: ["HbA1c", "Hct", "BF%", "ApoB", "CRP", "BP"],
            E2:    ["E2", "IGF1", "TT", "BP", "BF%"],
            // ── Inner-ring markers (no radial arm) ──
            IGF1:  ["IGF1", "E2", "HbA1c"],
            PSA:   ["PSA", "DHT"],
            GGT:   ["GGT"],
            FT:    ["FT", "TT", "SHBG", "DHT", "E2"],
            ApoB:  ["ApoB", "Hct", "BF%", "CRP", "HbA1c", "BP"],
            CysC:  ["CysC"],
            TSH:   ["TSH"],
            "BF%": ["BF%", "Hct", "ApoB", "CRP", "HbA1c", "BP"],
        };

        const el = rootRef.current;
        if (!el) return;
        const svg = el.querySelector(".cdo-center-svg");
        if (!svg) return;
        const nodes = svg.querySelectorAll(".cdo-node-inner[data-node]");
        const cleanup = [];
        nodes.forEach((node) => {
            const label = node.getAttribute("data-node");
            const groupLabels = HOVER_GROUPS[label] || [label];

            // Pre-resolve group nodes + their lines for fast hover handling
            const groupNodes = [];
            const groupLines = [];
            groupLabels.forEach((g) => {
                const n = svg.querySelector(
                    `.cdo-node-inner[data-node="${CSS.escape(g)}"]`
                );
                if (!n) return;
                groupNodes.push(n);
                const lk = n.getAttribute("data-line");
                if (lk) {
                    const ln = svg.querySelector(
                        `line.cdo-radial-line[data-line="${lk}"]`
                    );
                    if (ln) groupLines.push(ln);
                }
            });

            const onEnter = () => {
                svg.classList.add("cdo-hover-active");
                groupNodes.forEach((n) => n.classList.add("cdo-node-active"));
                groupLines.forEach((l) => l.classList.add("cdo-line-active"));
                setCaption(CAPTIONS[label] || { name: label, flow: "individual marker" });
                setIsActive(true);
            };
            const onLeave = () => {
                svg.classList.remove("cdo-hover-active");
                groupNodes.forEach((n) => n.classList.remove("cdo-node-active"));
                groupLines.forEach((l) => l.classList.remove("cdo-line-active"));
                setIsActive(false);
            };
            node.addEventListener("mouseenter", onEnter);
            node.addEventListener("mouseleave", onLeave);
            cleanup.push(() => {
                node.removeEventListener("mouseenter", onEnter);
                node.removeEventListener("mouseleave", onLeave);
            });
        });

        // Hub hover — central engine takeover. Dims all 16 nodes + the
        // background mesh, brightens the hub border, brightens the corner
        // formulas, and reverses the radial pulses so the engine appears
        // to broadcast outward to every marker.
        const hub = svg.querySelector(".cdo-hub-inner");
        if (hub) {
            const onHubEnter = () => {
                svg.classList.add("cdo-hover-active");
                svg.classList.add("cdo-hub-active");
                setCaption(HUB_CAPTION);
                setIsActive(true);
            };
            const onHubLeave = () => {
                svg.classList.remove("cdo-hover-active");
                svg.classList.remove("cdo-hub-active");
                setIsActive(false);
            };
            hub.addEventListener("mouseenter", onHubEnter);
            hub.addEventListener("mouseleave", onHubLeave);
            cleanup.push(() => {
                hub.removeEventListener("mouseenter", onHubEnter);
                hub.removeEventListener("mouseleave", onHubLeave);
            });
        }

        return () => cleanup.forEach((fn) => fn());
    }, []);

    // Center-only mode: render just the interactive schematic (the
    // hub + spider network + caption) with no flow strip, no
    // input/engine/output columns, no mini cards. Used by the
    // /engine/embed?center=1 route for the in-app modal reference.
    if (centerOnly) {
        return (
            <div
                ref={rootRef}
                className="cdo-schematic cdo-center-only"
                data-testid="engine-schematic-center-only"
            >
                <div className="cdo-stage cdo-stage-bare">
                    <CenterNetworkSVG />
                    <EngineCaption
                        caption={caption}
                        isActive={isActive}
                        isHub={caption === HUB_CAPTION}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            ref={rootRef}
            className="cdo-schematic"
            data-testid="engine-schematic"
        >
            <div className="cdo-page">
                {showHeader && (
                    <>
                        <div className="cdo-section-head">
                            <span className="cdo-pill">
                                <span className="cdo-eyebrow">ENGINE SCHEMATIC</span>
                            </span>
                            <h2 className="cdo-tagline">
                                The complexity is ours.
                                <br />
                                <span className="cdo-glow">
                                    The clarity is yours.
                                </span>
                            </h2>
                            <p className="cdo-subhead">
                                You give us your protocol and your bloodwork.
                                The engine does the rest.
                            </p>
                            <p className="cdo-subhead cdo-subhead-ii">
                                A standard blood test is one-dimensional — one
                                marker, one range. The TRT Plus engine reads
                                the clinical relationships between markers,
                                weighing ratios and dynamic interactions, not
                                just absolute values.
                            </p>
                        </div>

                        <div
                            className="cdo-flow-strip"
                            data-testid="engine-flow-strip"
                        >
                            <div className="cdo-flow-label">
                                <div className="cdo-flow-main">Inputs</div>
                                <div className="cdo-flow-sub">You provide</div>
                            </div>
                            <FlowArrow />
                            <div className="cdo-flow-label cdo-flow-engine">
                                <div className="cdo-flow-main">The Engine</div>
                                <div className="cdo-flow-sub">We built</div>
                            </div>
                            <FlowArrow />
                            <div className="cdo-flow-label">
                                <div className="cdo-flow-main">Outputs</div>
                                <div className="cdo-flow-sub">You receive</div>
                            </div>
                        </div>
                    </>
                )}

                <div className="cdo-grid">
                    {/* LEFT COLUMN */}
                    <div className="cdo-col">
                        <div className="cdo-flow-divider cdo-flow-label">
                            <div className="cdo-flow-main">Inputs</div>
                            <div className="cdo-flow-sub">You provide</div>
                            <FlowArrow />
                        </div>
                        {/* My Protocol */}
                        <div className="cdo-panel cdo-card-eq">
                            <svg className="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <rect x="5" y="4" width="14" height="17" rx="2" />
                                <path d="M9 4h6v3H9z" />
                                <path d="M8.5 11h7" />
                                <path d="M8.5 14h7" />
                                <path d="M8.5 17h4" />
                            </svg>
                            <div className="label">MY<br />PROTOCOL</div>
                            <p>Your individual protocol — dose, frequency, all compounds.</p>
                            <div className="cdo-protocol-stack">
                                <div className="cdo-protocol-group-label">Testosterone</div>
                                <div className="cdo-protocol-row">
                                    <span className="cdo-pplus">+</span>
                                    <span className="cdo-pname">Test Cyp</span>
                                    <span className="cdo-pemoji">💪🏼</span>
                                    <span className="cdo-pdose">140 mg/W (D)</span>
                                </div>
                                <div className="cdo-protocol-group-label">Compounds</div>
                                <div className="cdo-protocol-row">
                                    <span className="cdo-pplus">+</span>
                                    <span className="cdo-pname">GH</span>
                                    <span className="cdo-pemoji">🧬</span>
                                    <span className="cdo-pdose">1 IU (D)</span>
                                </div>
                                <div className="cdo-protocol-row">
                                    <span className="cdo-pplus">+</span>
                                    <span className="cdo-pname">Anavar</span>
                                    <span className="cdo-pemoji">🐂</span>
                                    <span className="cdo-pdose">20 mg (3xW)</span>
                                </div>
                                <div className="cdo-protocol-row">
                                    <span className="cdo-pplus">+</span>
                                    <span className="cdo-pname">BPC-157</span>
                                    <span className="cdo-pemoji">🐺</span>
                                    <span className="cdo-pdose">250 mcg (D)</span>
                                </div>
                                <div className="cdo-protocol-row">
                                    <span className="cdo-pplus">+</span>
                                    <span className="cdo-pname">Fish Oil</span>
                                    <span className="cdo-pemoji">🐟</span>
                                    <span className="cdo-pdose">6 g (D)</span>
                                </div>
                            </div>
                        </div>

                        {/* My Biomarkers */}
                        <div className="cdo-panel cdo-card-eq">
                            <svg className="icon" viewBox="0 0 32 28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M16 25 C 16 25, 3 17, 3 9 C 3 5, 6 2, 10 2 C 12.5 2, 14.8 3.3, 16 5.3 C 17.2 3.3, 19.5 2, 22 2 C 26 2, 29 5, 29 9 C 29 11, 28.3 13, 27 14.6 L 24 14.6 L 21.6 11 L 18.5 18.5 L 14.5 8 L 12 14.6 L 8 14.6" />
                            </svg>
                            <div className="label">MY<br />BIOMARKERS</div>
                            <p>Comprehensive 15+ biomarker panels analyzed against optimized reference ranges.</p>
                            <div className="cdo-bm-grid">
                                <BMCard name="IGF-1" tight pill="L" pillTone="amber" val="204" valTone="amber" unit="ng" />
                                <BMCard name="Total T" tight val="1278" unit="ng/dL" />
                                <BMCard name="E2" val="41" unit="pg/mL" />
                                <BMCard name="Hct" val="54" unit="%" />
                                <BMCard name="BP" valHTML={<>120<span className="cdo-bm-sep">/</span>79</>} />
                                <BMCard name="ApoB" val="0.7" unit="g/L" />
                                <BMCard name="Body Fat" val="8.8" unit="%" />
                                <BMCard name="TSH" val="1.3" unit="mIU/L" />
                                <BMCard name="CRP" val="1.4" unit="mg/L" />
                                <BMCard name="SHBG" val="35" unit="nmol/L" />
                                <BMCard name="DHT" val="47" unit="ng/dL" />
                                <BMCard name="PSA" val="0.78" unit="ug/L" />
                                <BMCard name="HbA1c" val="4.9" unit="%" />
                                <BMCard name="GGT" val="22" unit="U/L" />
                                <BMCard name="CysC" val="0.68" unit="mg/L" />
                            </div>
                        </div>
                    </div>

                    {/* CENTER STAGE */}
                    <div className="cdo-stage">
                        <div className="cdo-flow-divider cdo-flow-label cdo-flow-engine">
                            <div className="cdo-flow-main">The Engine</div>
                            <div className="cdo-flow-sub">We built</div>
                            <FlowArrow />
                        </div>
                        <div className="cdo-top-row">
                            <div className="cdo-mini wide">
                                <svg className="micon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <circle cx="12" cy="7" r="2.4" />
                                    <path d="M7.5 19c0-2.5 2-4.4 4.5-4.4S16.5 16.5 16.5 19" />
                                    <circle cx="5" cy="9" r="1.8" />
                                    <path d="M2 18.2c0-2 1.4-3.6 3.2-3.6" />
                                    <circle cx="19" cy="9" r="1.8" />
                                    <path d="M22 18.2c0-2-1.4-3.6-3.2-3.6" />
                                </svg>
                                <div className="text">
                                    <h4>Deep Analysis of<br />Real-World TRT Patients</h4>
                                    <p>De-identified results form the foundation of every model and reference range.</p>
                                </div>
                            </div>
                            <div className="cdo-mini wide">
                                <svg className="micon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M12 3 5 6v6c0 4.5 3 7.6 7 9 4-1.4 7-4.5 7-9V6l-7-3z" />
                                    <rect x="9.5" y="11" width="5" height="4.5" rx="0.6" />
                                    <path d="M10.5 11V9.7a1.5 1.5 0 0 1 3 0V11" />
                                </svg>
                                <div className="text">
                                    <h4>Data Foundation</h4>
                                    <p className="cdo-inline-list">HIPAA-compliant · De-identified · Encrypted · Secure</p>
                                </div>
                            </div>
                        </div>

                        <CenterNetworkSVG />

                        <EngineCaption
                            caption={caption}
                            isActive={isActive}
                            isHub={caption === HUB_CAPTION}
                        />

                        <div className="cdo-mini-row">
                            <div className="cdo-mini">
                                <svg className="micon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="12,3 21,9.5 17.5,20 6.5,20 3,9.5" />
                                    <circle cx="12" cy="3" r="1.4" fill="currentColor" />
                                    <circle cx="21" cy="9.5" r="1.4" fill="currentColor" />
                                    <circle cx="17.5" cy="20" r="1.4" fill="currentColor" />
                                    <circle cx="6.5" cy="20" r="1.4" fill="currentColor" />
                                    <circle cx="3" cy="9.5" r="1.4" fill="currentColor" />
                                    <line x1="12" y1="3" x2="17.5" y2="20" />
                                    <line x1="12" y1="3" x2="6.5" y2="20" />
                                    <line x1="3" y1="9.5" x2="17.5" y2="20" />
                                    <line x1="21" y1="9.5" x2="6.5" y2="20" />
                                </svg>
                                <h4>Clinical Framework</h4>
                                <p>Evidence-based protocols from real clinician input.</p>
                            </div>
                            <div className="cdo-mini">
                                <svg className="micon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 4.5a2.5 2.5 0 0 0-2.5 2.5v.5A2.5 2.5 0 0 0 5 9.5v1A2.5 2.5 0 0 0 6.5 13v.5A2.5 2.5 0 0 0 9 16h0v3" />
                                    <path d="M15 4.5A2.5 2.5 0 0 1 17.5 7v.5A2.5 2.5 0 0 1 19 9.5v1A2.5 2.5 0 0 1 17.5 13v.5A2.5 2.5 0 0 1 15 16h0v3" />
                                    <path d="M9 4.5h6" />
                                    <path d="M9 12h6" />
                                </svg>
                                <h4>AI-Enhanced</h4>
                                <p>Pattern recognition across thousands of cases.</p>
                            </div>
                            <div className="cdo-mini">
                                <svg className="micon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 12a8 8 0 0 1-13.5 5.8" />
                                    <path d="M4 12a8 8 0 0 1 13.5-5.8" />
                                    <polyline points="17.5,2.5 17.5,6.5 13.5,6.5" />
                                    <polyline points="6.5,21.5 6.5,17.5 10.5,17.5" />
                                </svg>
                                <h4>Continuous Learning</h4>
                                <p>Models refine as new data &amp; studies flow in.</p>
                            </div>
                            <div className="cdo-mini">
                                <svg className="micon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 3 L5 6 v6 c0 4.5 3 7.6 7 9 4-1.4 7-4.5 7-9 V6 l-7-3z" />
                                    <path d="M9 12 l2.5 2.5 L15 10" />
                                </svg>
                                <h4>Healthspan Advocacy</h4>
                                <p>Promotes real bloodwork follow-through and whole-health vigilance.</p>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN */}
                    <div className="cdo-col">
                        <div className="cdo-flow-divider cdo-flow-label">
                            <div className="cdo-flow-main">Outputs</div>
                            <div className="cdo-flow-sub">You receive</div>
                            <FlowArrow />
                        </div>
                        {/* Biomarker Score & Ratios */}
                        <div className="cdo-panel cdo-card-eq flex-shrink">
                            <div className="cdo-right-card-title">BIOMARKER SCORE<br />&amp; RATIOS</div>
                            <div className="cdo-gauge-wrap">
                                <svg viewBox="0 0 180 100" width="100%" aria-hidden="true">
                                    <defs>
                                        <linearGradient id="cdoGaugeArc" x1="0" x2="1">
                                            <stop offset="0" stopColor="#FF5252" />
                                            <stop offset="0.18" stopColor="#FF8A47" />
                                            <stop offset="0.32" stopColor="#FFB347" />
                                            <stop offset="0.46" stopColor="#9CE07A" />
                                            <stop offset="0.55" stopColor="#00E6A0" />
                                            <stop offset="1" stopColor="#00E6A0" />
                                        </linearGradient>
                                        <linearGradient id="cdoRatioArc" x1="0" x2="1">
                                            <stop offset="0" stopColor="#FF5252" />
                                            <stop offset="0.14" stopColor="#FFB347" />
                                            <stop offset="0.32" stopColor="#9CE07A" />
                                            <stop offset="0.50" stopColor="#00E6A0" />
                                            <stop offset="0.68" stopColor="#9CE07A" />
                                            <stop offset="0.86" stopColor="#FFB347" />
                                            <stop offset="1" stopColor="#FF5252" />
                                        </linearGradient>
                                    </defs>
                                    <path d="M15 90 A 75 75 0 0 1 165 90" stroke="rgba(255,255,255,0.08)" strokeWidth="14" fill="none" strokeLinecap="round" />
                                    <path d="M15 90 A 75 75 0 0 1 165 90" stroke="rgba(255,255,255,0.07)" strokeWidth="14" fill="none" strokeLinecap="round" />
                                    <path d="M15 90 A 75 75 0 0 1 165 90" stroke="url(#cdoGaugeArc)" strokeWidth="14" fill="none" strokeLinecap="round" strokeDasharray="229.97 235.62" />
                                    <text x="90" y="60" textAnchor="middle" fontFamily="Manrope, sans-serif" fontSize="10" fill="rgba(255,255,255,0.55)" letterSpacing="0.4">Overall Score</text>
                                    <text x="90" y="86" textAnchor="middle" fontFamily="Exo 2, sans-serif" fontWeight="800" fontSize="30" fill="#F4F5F7">976</text>
                                </svg>
                            </div>
                            <div className="cdo-ratio-row">
                                <RatioCell num="37:1" label="T:SHBG" tone="optimal" toneLabel="Optimal" />
                                <RatioCell num="28:1" label="A:E" tone="balanced" toneLabel="Balanced" />
                            </div>
                        </div>

                        {/* Personalized Biomarkers Report */}
                        <div className="cdo-panel cdo-card-eq flex-grow">
                            <div className="cdo-report-head">
                                <div className="cdo-ai-chip" aria-hidden="true">
                                    <div className="cdo-ai-chip-inner">Ai</div>
                                </div>
                                <div className="cdo-right-card-title">PERSONALIZED BIOMARKERS<br />REPORT</div>
                            </div>
                            <ul className="cdo-bullets cdo-plus-bullets">
                                <li>Recommendations</li>
                                <li>Protocol & Dose Response</li>
                                <li>Hormonal Balance</li>
                                <li>Muscle-Building Potential</li>
                                <li>Compound Analysis</li>
                                <li>Healthspan Assessment</li>
                                <li>Cardiovascular Risk Profile</li>
                                <li>Overall Health</li>
                            </ul>
                        </div>

                        {/* Optimized Steady-State chart */}
                        <div className="cdo-panel cdo-card-eq flex-shrink">
                            <div className="cdo-right-card-title cdo-testimator-title">
                                <span className="cdo-testimator">The Testimator<sup>TM</sup></span>
                                <br />OPTIMIZED STEADY-STATE
                            </div>
                            <div className="cdo-chart-wrap">
                                <SteadyStateChartSVG />
                            </div>
                            <div className="cdo-chart-caption">Read your response curve — dial in your protocol.</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function BMCard({ name, val, valHTML, valTone, unit, tight, pill, pillTone }) {
    return (
        <div className="cdo-bm-card">
            <div className="cdo-bm-head">
                <span className="cdo-bm-name">{name}</span>
            </div>
            <div className={`cdo-bm-row${tight ? " tight" : ""}`}>
                {pill && (
                    <span className={`cdo-bm-pill${pillTone ? ` ${pillTone}` : ""}`}>{pill}</span>
                )}
                <span className={`cdo-bm-val${valTone ? ` ${valTone}` : ""}`}>
                    {valHTML || val}
                </span>
                {unit && <span className="cdo-bm-unit">{unit}</span>}
            </div>
        </div>
    );
}

function RatioCell({ num, label, tone, toneLabel }) {
    return (
        <div className="cdo-ratio-cell">
            <svg viewBox="0 0 100 56" width="100%" aria-hidden="true">
                <path d="M8 50 A 42 42 0 0 1 92 50" stroke="rgba(255,255,255,0.08)" strokeWidth="8" fill="none" strokeLinecap="round" />
                <path d="M8 50 A 42 42 0 0 1 92 50" stroke="url(#cdoRatioArc)" strokeWidth="8" fill="none" strokeLinecap="round" />
                <g transform="translate(50,50) rotate(0)">
                    <line x1="0" y1="0" x2="0" y2="-36" stroke="#00E6A0" strokeWidth="2" strokeLinecap="round" style={{ filter: "drop-shadow(0 0 4px rgba(0,230,160,0.7))" }} />
                    <circle r="3" fill="#00E6A0" style={{ filter: "drop-shadow(0 0 4px rgba(0,230,160,0.7))" }} />
                </g>
            </svg>
            <div className="cdo-ratio-num">{num}</div>
            <div className="cdo-ratio-label">{label}</div>
            <div className={`cdo-ratio-label ${tone}`}>{toneLabel}</div>
        </div>
    );
}

function CenterNetworkSVG() {
    return (
        <svg className="cdo-center-svg" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" viewBox="0 40 560 540" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
            <defs>
                <radialGradient id="cdoHubGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#49BEF5" stopOpacity="0.8" />
                    <stop offset="60%" stopColor="#2BA6E0" stopOpacity="0.15" />
                    <stop offset="100%" stopColor="#2BA6E0" stopOpacity="0" />
                </radialGradient>
                <radialGradient id="cdoNodeGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#49BEF5" stopOpacity="0.55" />
                    <stop offset="70%" stopColor="#2BA6E0" stopOpacity="0.08" />
                    <stop offset="100%" stopColor="#2BA6E0" stopOpacity="0" />
                </radialGradient>
                <filter id="cdoSoftGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="2.4" result="b" />
                    <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <pattern id="cdoGrid" width="28" height="28" patternUnits="userSpaceOnUse">
                    <path d="M28 0H0V28" fill="none" stroke="rgba(73,190,245,0.04)" strokeWidth="0.5" />
                </pattern>
                <clipPath id="cdoAppIconClip">
                    <rect x="-58" y="-58" width="116" height="116" rx="22" />
                </clipPath>
                <radialGradient id="cdoHubFill" cx="35%" cy="30%" r="80%">
                    <stop offset="0%" stopColor="#1a2c44" />
                    <stop offset="55%" stopColor="#0d1a2c" />
                    <stop offset="100%" stopColor="#06121f" />
                </radialGradient>
            </defs>

            <rect x="0" y="0" width="560" height="620" fill="url(#cdoGrid)" />

            <g fontFamily="JetBrains Mono, monospace" fill="rgba(255,255,255,0.10)" fontSize="11">
                <text className="cdo-formula" style={{ "--cdo-delay": "2.00s" }} x="40" y="40">C(τ) = (F·D·kₐ) / V</text>
                <text className="cdo-formula" style={{ "--cdo-delay": "2.05s" }} x="380" y="40">Sᵢ = 1 / (1 + e^-k(zᵢ-z*))</text>
                <text className="cdo-formula" style={{ "--cdo-delay": "2.10s" }} x="36" y="90">I_accum = 1/(kₑ-kₐ)</text>
                <text className="cdo-formula" style={{ "--cdo-delay": "2.15s" }} x="400" y="100">t_max = τ</text>
                <text className="cdo-formula" style={{ "--cdo-delay": "2.20s" }} x="30" y="160">Zᵢ = (xᵢ - μᵢ) / σᵢ</text>
                <text className="cdo-formula" style={{ "--cdo-delay": "2.25s" }} x="400" y="180">w = (R + λI)⁻¹</text>
                <text className="cdo-formula" style={{ "--cdo-delay": "2.30s" }} x="40" y="520">rᵢⱼ = Cov(xᵢ,xⱼ)/σᵢσⱼ</text>
                <text className="cdo-formula" style={{ "--cdo-delay": "2.35s" }} x="380" y="520">Score = Σ wᵢ·Sᵢ</text>
                <text className="cdo-formula" style={{ "--cdo-delay": "2.40s" }} x="30" y="570">S_adj = Sᵢ·(1+Σβᵢⱼ·Zⱼ)</text>
                <text className="cdo-formula" style={{ "--cdo-delay": "2.45s" }} x="380" y="570">λ = regularisation</text>
            </g>

            <g transform="translate(280 310)">
                <circle r="220" fill="url(#cdoHubGlow)" opacity="0.55" />
                <circle r="140" fill="url(#cdoHubGlow)" opacity="0.4" />

                <g stroke="rgba(73,190,245,0.18)" strokeWidth="0.6" fill="none">
                    <line x1="-180" y1="-150" x2="180" y2="150" />
                    <line x1="180" y1="-150" x2="-180" y2="150" />
                    <line x1="-200" y1="0" x2="200" y2="0" />
                    <line x1="0" y1="-180" x2="0" y2="180" />
                    <line x1="-160" y1="-130" x2="160" y2="-130" />
                    <line x1="-160" y1="130" x2="160" y2="130" />
                    <line x1="-200" y1="-60" x2="200" y2="60" />
                    <line x1="-200" y1="60" x2="200" y2="-60" />
                    <line x1="-110" y1="-160" x2="110" y2="160" />
                    <line x1="110" y1="-160" x2="-110" y2="160" />
                </g>

                <g stroke="#49BEF5" strokeWidth="1" fill="none" filter="url(#cdoSoftGlow)">
                    <line className="cdo-radial-line" data-line="n" style={{ "--cdo-delay": "1.20s" }} x1="0" y1="0" x2="0" y2="-210" />
                    <line className="cdo-radial-line" data-line="ne" style={{ "--cdo-delay": "1.25s" }} x1="0" y1="0" x2="188" y2="-115" />
                    <line className="cdo-radial-line" data-line="e" style={{ "--cdo-delay": "1.30s" }} x1="0" y1="0" x2="220" y2="5" />
                    <line className="cdo-radial-line" data-line="se" style={{ "--cdo-delay": "1.35s" }} x1="0" y1="0" x2="170" y2="135" />
                    <line className="cdo-radial-line" data-line="s" style={{ "--cdo-delay": "1.40s" }} x1="0" y1="0" x2="0" y2="215" />
                    <line className="cdo-radial-line" data-line="sw" style={{ "--cdo-delay": "1.45s" }} x1="0" y1="0" x2="-170" y2="135" />
                    <line className="cdo-radial-line" data-line="w" style={{ "--cdo-delay": "1.50s" }} x1="0" y1="0" x2="-220" y2="5" />
                    <line className="cdo-radial-line" data-line="nw" style={{ "--cdo-delay": "1.55s" }} x1="0" y1="0" x2="-188" y2="-115" />
                </g>

                {/* Tier 2 data-flow pulses — small bright dashes traveling outer→hub, looping */}
                <g stroke="#49BEF5" strokeWidth="2" fill="none" filter="url(#cdoSoftGlow)" strokeLinecap="round">
                    <line className="cdo-radial-pulse" data-line="n" style={{ "--cdo-delay": "0s" }} x1="0" y1="0" x2="0" y2="-210" />
                    <line className="cdo-radial-pulse" data-line="ne" style={{ "--cdo-delay": "0.4s" }} x1="0" y1="0" x2="188" y2="-115" />
                    <line className="cdo-radial-pulse" data-line="e" style={{ "--cdo-delay": "0.8s" }} x1="0" y1="0" x2="220" y2="5" />
                    <line className="cdo-radial-pulse" data-line="se" style={{ "--cdo-delay": "1.2s" }} x1="0" y1="0" x2="170" y2="135" />
                    <line className="cdo-radial-pulse" data-line="s" style={{ "--cdo-delay": "1.6s" }} x1="0" y1="0" x2="0" y2="215" />
                    <line className="cdo-radial-pulse" data-line="sw" style={{ "--cdo-delay": "2.0s" }} x1="0" y1="0" x2="-170" y2="135" />
                    <line className="cdo-radial-pulse" data-line="w" style={{ "--cdo-delay": "2.4s" }} x1="0" y1="0" x2="-220" y2="5" />
                    <line className="cdo-radial-pulse" data-line="nw" style={{ "--cdo-delay": "2.8s" }} x1="0" y1="0" x2="-188" y2="-115" />
                </g>

                <g stroke="rgba(73,190,245,0.30)" strokeWidth="0.8" fill="none">
                    <polygon className="cdo-mesh-line" style={{ "--cdo-delay": "1.65s" }} points="0,-210 188,-115 220,5 170,135 0,215 -170,135 -220,5 -188,-115" />
                    <line className="cdo-mesh-line" style={{ "--cdo-delay": "1.70s" }} x1="0" y1="-210" x2="220" y2="5" />
                    <line className="cdo-mesh-line" style={{ "--cdo-delay": "1.75s" }} x1="0" y1="-210" x2="-220" y2="5" />
                    <line className="cdo-mesh-line" style={{ "--cdo-delay": "1.80s" }} x1="188" y1="-115" x2="170" y2="135" />
                    <line className="cdo-mesh-line" style={{ "--cdo-delay": "1.85s" }} x1="-188" y1="-115" x2="-170" y2="135" />
                    <line className="cdo-mesh-line" style={{ "--cdo-delay": "1.90s" }} x1="220" y1="5" x2="-170" y2="135" />
                    <line className="cdo-mesh-line" style={{ "--cdo-delay": "1.95s" }} x1="-220" y1="5" x2="170" y2="135" />
                    {/* Biological relationship loop: E2 → IGF1 → DHT → Hct → ApoB → CRP → E2 */}
                    <polygon className="cdo-mesh-line cdo-rel-loop" style={{ "--cdo-delay": "2.00s" }} points="-188,-115 0,-132 188,-115 170,135 0,130 -170,135" />
                </g>

                {/* Tier 2 — subtle circular "chasing" dashes orbiting the
                    outer octagon perimeter and the inner dashed ring,
                    reinforcing the interconnected-network feel. */}
                <g stroke="#49BEF5" fill="none" filter="url(#cdoSoftGlow)" strokeLinecap="round">
                    <polygon
                        className="cdo-mesh-chase"
                        strokeWidth="1.2"
                        points="0,-210 188,-115 220,5 170,135 0,215 -170,135 -220,5 -188,-115"
                    />
                    <circle
                        className="cdo-ring-chase"
                        strokeWidth="0.8"
                        r="132"
                    />
                </g>

                {/* Mint chase along the biological relationship hex-loop
                    (E2 → IGF1 → DHT → Hct → ApoB → CRP → E2). Different
                    color from the blue geometric chases so the loop
                    reads as "biological relationship" not "geometry". */}
                <g stroke="#00E6A0" fill="none" filter="url(#cdoSoftGlow)" strokeLinecap="round">
                    <polygon
                        className="cdo-rel-loop-chase"
                        strokeWidth="1"
                        points="-188,-115 0,-132 188,-115 170,135 0,130 -170,135"
                    />
                </g>

                <g className="cdo-inner-ring" stroke="rgba(73,190,245,0.22)" strokeWidth="0.6" fill="none" strokeDasharray="2 2">
                    <circle r="132" />
                </g>

                <g fontFamily="JetBrains Mono, monospace" fontWeight="500" fontSize="12" fill="#F4F5F7" textAnchor="middle">
                    <NetNode x={0} y={-132} label="IGF1" delay={0.00} />
                    <NetNode x={118} y={-72} label="PSA" delay={0.06} />
                    <NetNode x={132} y={4} label="GGT" delay={0.12} />
                    <NetNode x={103} y={82} label="FT" delay={0.18} />
                    <NetNode x={0} y={130} label="ApoB" delay={0.24} />
                    <NetNode x={-103} y={82} label="CysC" delay={0.30} />
                    <NetNode x={-132} y={4} label="TSH" delay={0.36} />
                    <NetNode x={-118} y={-72} label="BF%" delay={0.42} />
                </g>

                <g fontFamily="JetBrains Mono, monospace" fontWeight="500" fontSize="12" fill="#F4F5F7" textAnchor="middle">
                    <NetNode x={0} y={-210} label="TT" delay={0.50} lineKey="n" />
                    <NetNode x={188} y={-115} label="DHT" delay={0.56} lineKey="ne" />
                    <NetNode x={220} y={5} label="SHBG" delay={0.62} lineKey="e" />
                    <NetNode x={170} y={135} label="Hct" delay={0.68} lineKey="se" />
                    <NetNode x={0} y={215} label="BP" delay={0.74} lineKey="s" />
                    <NetNode x={-170} y={135} label="CRP" delay={0.80} lineKey="sw" />
                    <NetNode x={-220} y={5} label="HbA1c" delay={0.86} lineKey="w" />
                    <NetNode x={-188} y={-115} label="E2" delay={0.92} lineKey="nw" />
                </g>

                <g>
                    <g className="cdo-hub-inner">
                        <rect x="-64" y="-64" width="128" height="128" rx="28" fill="url(#cdoHubFill)" stroke="#49BEF5" strokeWidth="1.6" filter="url(#cdoSoftGlow)" />
                        <rect x="-60" y="-60" width="120" height="120" rx="24" fill="none" stroke="rgba(73,190,245,0.35)" strokeWidth="1" />
                        <image
                            href={ASSETS.icon}
                            x="-58"
                            y="-58"
                            width="116"
                            height="116"
                            preserveAspectRatio="xMidYMid slice"
                            clipPath="url(#cdoAppIconClip)"
                        />
                    </g>
                </g>
            </g>
        </svg>
    );
}

function EngineCaption({ caption, isActive, isHub }) {
    /* Live terminal readout. The `>` prompt is permanent (gently
       blinking gray when idle, brightens to mint when active). Row 1
       (label + axis name) appears instantly on hover — labels don't
       type. Row 2 (the marker flow) types in like a live readout. */
    const PREFIX = "ENGINE PROCESSING:";
    const flowTarget = isActive ? caption.flow : "";
    const flowText = useTypewriter(flowTarget, isActive, 260);

    return (
        <div
            className={`cdo-caption-inline${isHub ? " hub" : ""}${!isActive ? " idle" : ""}`}
            data-testid="engine-processing-caption"
            aria-live="polite"
        >
            <div className="cdo-caption-row cdo-caption-row-1">
                <span className="cdo-caption-prompt" aria-hidden="true">
                    {">"}
                </span>
                {isActive && (
                    <>
                        <span className="cdo-caption-prefix">{PREFIX}</span>
                        <span className="cdo-caption-name">{caption.name}</span>
                    </>
                )}
            </div>
            <div className="cdo-caption-row cdo-caption-row-2">
                {isActive && (
                    <span className="cdo-caption-flow">{flowText}</span>
                )}
            </div>
        </div>
    );
}



function FlowArrow() {
    return (
        <div className="cdo-flow-arrow-wrap">
            <svg width="44" height="14" viewBox="0 0 44 14" fill="none" aria-hidden="true">
                <defs>
                    <linearGradient id="cdoFlowArrowGrad" x1="0" x2="1">
                        <stop offset="0" stopColor="#2BA6E0" stopOpacity="0.35" />
                        <stop offset="1" stopColor="#49BEF5" stopOpacity="1" />
                    </linearGradient>
                </defs>
                <line x1="0" y1="7" x2="36" y2="7" stroke="url(#cdoFlowArrowGrad)" strokeWidth="1.6" strokeLinecap="round" />
                <path d="M32 2 L40 7 L32 12" stroke="#49BEF5" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </div>
    );
}

function NetNode({ x, y, label, delay = 0, lineKey }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <g
                className="cdo-node-inner"
                data-node={label}
                {...(lineKey ? { "data-line": lineKey } : {})}
                style={{ "--cdo-delay": `${delay}s` }}
            >
                <circle r="34" fill="url(#cdoNodeGlow)" />
                <circle r="22" fill="#06070A" stroke="#49BEF5" strokeWidth="1.4" filter="url(#cdoSoftGlow)" />
                <text y="5">{label}</text>
            </g>
        </g>
    );
}

function SteadyStateChartSVG() {
    return (
        <svg viewBox="6 0 234 130" aria-hidden="true">
            <defs>
                <linearGradient id="cdoOscGrad" x1="0" x2="1">
                    <stop offset="0" stopColor="#FF5252" />
                    <stop offset="0.20" stopColor="#FF7A45" />
                    <stop offset="0.38" stopColor="#FFB347" />
                    <stop offset="0.50" stopColor="#FFE066" />
                    <stop offset="0.62" stopColor="#9CE07A" />
                    <stop offset="0.75" stopColor="#00E6A0" />
                    <stop offset="1" stopColor="#00E6A0" />
                </linearGradient>
                <filter id="cdoCurveGlow"><feGaussianBlur stdDeviation="0.7" /></filter>
            </defs>
            <line x1="22" y1="10" x2="22" y2="108" stroke="rgba(255,255,255,0.18)" strokeWidth="0.6" />
            <line x1="22" y1="108" x2="234" y2="108" stroke="rgba(255,255,255,0.18)" strokeWidth="0.6" />
            <rect x="22" y="44" width="212" height="32" fill="none" stroke="rgba(0,230,160,0.35)" strokeWidth="0.6" strokeDasharray="3 3" />
            <text className="cdo-chart-axis" x="232" y="42" textAnchor="end">Upper Optimal Range</text>
            <text className="cdo-chart-axis" x="232" y="86" textAnchor="end">Lower Optimal Range</text>
            <g className="cdo-chart-axis" textAnchor="middle">
                <text x="22" y="120">0</text>
                <text x="49" y="120">7</text>
                <text x="76" y="120">14</text>
                <text x="103" y="120">21</text>
                <text x="130" y="120">28</text>
                <text x="157" y="120">35</text>
                <text x="184" y="120">42</text>
                <text x="211" y="120">49</text>
                <text x="232" y="120">56</text>
            </g>
            <text className="cdo-chart-axis-title" x="130" y="130" textAnchor="middle">Time (days)</text>
            <path d="M 22 60 C 26 60, 28 18, 35 18 C 42 18, 44 86, 49 86 C 54 86, 56 18, 62 18 C 68 18, 70 86, 76 86 C 78.8 86, 79.6 44, 81.6 44 C 83.6 44, 84.4 76, 87.1 76 C 89.9 76, 90.7 44, 92.7 44 C 94.7 44, 95.5 76, 98.3 76 C 101 76, 101.9 44, 103.9 44 C 105.9 44, 106.7 76, 109.4 76 C 112.2 76, 113 44, 115 44 C 117 44, 117.9 76, 120.6 76 C 123.4 76, 124.2 44, 126.2 44 C 128.2 44, 129 76, 131.7 76 C 134.5 76, 135.4 44, 137.4 44 C 139.4 44, 140.2 76, 142.9 76 C 145.7 76, 146.5 44, 148.5 44 C 150.5 44, 151.3 76, 154 76 C 156.8 76, 157.6 44, 159.6 44 C 161.6 44, 162.5 76, 165.2 76 C 168 76, 168.8 44, 170.8 44 C 172.8 44, 173.6 76, 176.3 76 C 179.1 76, 179.9 44, 181.9 44 C 183.9 44, 184.8 76, 187.5 76 C 190.3 76, 191.1 44, 193.1 44 C 195.1 44, 195.9 76, 198.6 76 C 201.4 76, 202.3 44, 204.3 44 C 206.3 44, 207.1 76, 209.8 76 C 212.6 76, 213.4 44, 215.4 44 C 217.4 44, 218.2 76, 220.9 76 C 223.7 76, 224.6 44, 226.6 44 C 228.6 44, 230 60, 234 60" fill="none" stroke="url(#cdoOscGrad)" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" filter="url(#cdoCurveGlow)" />
        </svg>
    );
}
